import { useEffect, useRef, useState } from "react";
import { useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter,
  AlertDialogHeader, AlertDialogTitle,
} from "../components/ui/alert-dialog";

// Questions de l'app, jamais les boîtes du navigateur (window.confirm / window.prompt) : Alex 2026-10-26, « une popup
// du navigateur au lieu de la nôtre », pas présentable à une productrice. Utilisable partout, hooks compris :
//   if (!(await appConfirm({ body: t("…"), danger: true, ok: t("common.delete") }))) return;
//   const name = await appPrompt({ title: t("…"), defaultValue: b.name });   // null = annulé ou vide
// <AppDialogHost /> est monté une fois dans App (sous le routeur). Sans hôte monté (tests unitaires d'un composant
// seul), on retombe sur window.confirm / window.prompt.
// Clavier : Entrée valide une question ordinaire (le bouton OK a le focus) ; pour une suppression (`danger`), le
// focus reste sur « Annuler » — plus sûr que la boîte du navigateur. Changer de page répond « non ».

let host = null;            // fonction posée par l'hôte monté : (request) => void

function ask(request) {
  if (!host) {
    return Promise.resolve(request.kind === "prompt"
      ? window.prompt(request.body || request.title || "", request.defaultValue || "")
      : window.confirm(request.body || request.title || ""));
  }
  return new Promise((resolve) => host({ ...request, resolve }));
}

export const appConfirm = (opts) => ask({ kind: "confirm", ...opts });
export const appPrompt = (opts) => ask({ kind: "prompt", ...opts });

const NO = (req) => (req?.kind === "prompt" ? null : false);

export function AppDialogHost() {
  const { t } = useTranslation();
  const location = useLocation();
  // `req` garde le contenu pendant l'animation de fermeture ; `open` seul décide de l'affichage
  const [req, setReq] = useState(null);
  const [open, setOpen] = useState(false);
  const [value, setValue] = useState("");
  const current = useRef(null);       // question en attente de réponse (résolue une seule fois)
  const inputRef = useRef(null);
  const okRef = useRef(null);
  const answer = (result) => {
    const pending = current.current;
    current.current = null;
    pending?.resolve(result);
    setOpen(false);
  };
  useEffect(() => {
    const fn = (request) => {
      if (current.current) current.current.resolve(NO(current.current));   // la nouvelle remplace l'ancienne : « non »
      current.current = request;
      setValue(request.defaultValue || "");
      setReq(request);
      setOpen(true);
    };
    host = fn;
    return () => {
      if (host === fn) host = null;
      if (current.current) { current.current.resolve(NO(current.current)); current.current = null; }
    };
  }, []);
  // changer de page (bouton Précédent, navigation par le code) : la question de la page quittée répond « non » ;
  // un simple changement de paramètres d'URL sur la même page ne la ferme pas
  useEffect(() => { if (current.current) answer(NO(current.current)); }, [location.pathname]);   // eslint-disable-line react-hooks/exhaustive-deps
  const submit = () => {
    if (!current.current) return;
    if (current.current.kind === "prompt") {
      const v = value.trim();
      if (v) answer(v);
    } else {
      answer(true);
    }
  };
  const isPrompt = req?.kind === "prompt";
  return (
    <AlertDialog open={open} onOpenChange={(o) => { if (!o && current.current) answer(NO(current.current)); }}>
      <AlertDialogContent className="max-h-[85vh] overflow-y-auto border-neutral-800 bg-neutral-950 text-neutral-200" data-testid="app-dialog"
        {...(req?.body ? {} : { "aria-describedby": undefined })}
        onOpenAutoFocus={(e) => {
          if (isPrompt) { e.preventDefault(); inputRef.current?.focus(); inputRef.current?.select(); }
          else if (!req?.danger) { e.preventDefault(); okRef.current?.focus(); }
        }}>
        <AlertDialogHeader>
          <AlertDialogTitle className="text-neutral-50">{req?.title || t("common.confirm")}</AlertDialogTitle>
          {req?.body && <AlertDialogDescription className="whitespace-pre-line text-neutral-400">{req.body}</AlertDialogDescription>}
        </AlertDialogHeader>
        {isPrompt && (
          <input ref={inputRef} value={value} onChange={(e) => setValue(e.target.value)} data-testid="app-dialog-input"
            aria-label={req?.title || t("common.confirm")}
            onKeyDown={(e) => { if (e.key === "Enter" && !e.nativeEvent?.isComposing) { e.preventDefault(); submit(); } }}
            className="w-full rounded border border-neutral-700 bg-neutral-900 px-2 py-1.5 text-sm text-neutral-100" />
        )}
        <AlertDialogFooter>
          <AlertDialogCancel onClick={() => answer(NO(req))} data-testid="app-dialog-cancel">{req?.cancel || t("common.cancel")}</AlertDialogCancel>
          <AlertDialogAction ref={okRef} data-testid="app-dialog-ok" disabled={isPrompt && !value.trim()}
            onClick={(e) => { if (isPrompt && !value.trim()) { e.preventDefault(); return; } submit(); }}
            className={req?.danger ? "bg-red-600 text-white hover:bg-red-500" : "bg-violet-600 text-white hover:bg-violet-500"}>
            {req?.ok || t("common.confirm")}</AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}
